import { createElement } from "react";
import { createRoute, lazyRouteComponent } from "@tanstack/react-router";

import { requirePermission } from "../../../../shared/routing/requirePermission";
import {
  DRIVERS_CREATE,
  DRIVERS_UPDATE,
  DRIVERS_VIEW,
} from "../../usersPermissions";
import {
  archivedDriversSearchSchema,
  driversSearchSchema,
} from "./searchSchemas";
import type { UsersRouteFactory } from "./usersRouteContext";

const DriversListScreen = lazyRouteComponent(
  () => import("../DriversListScreen"),
  "DriversListScreen",
);
const ArchivedDriversListScreen = lazyRouteComponent(
  () => import("../ArchivedDriversListScreen"),
  "ArchivedDriversListScreen",
);
const DriverCreateScreen = lazyRouteComponent(
  () => import("../DriverCreateScreen"),
  "DriverCreateScreen",
);
const DriverEditScreen = lazyRouteComponent(
  () => import("../DriverEditScreen"),
  "DriverEditScreen",
);
const DriverDetailScreen = lazyRouteComponent(
  () => import("../DriverDetailScreen"),
  "DriverDetailScreen",
);

export const createDriversRoutes: UsersRouteFactory = ({
  context,
  drivers,
  companies,
  branches,
}) => {
  // basePath is a runtime string, so the template loses the literal type
  // TanStack Router needs to type params -- $id is guaranteed by the path
  // (same as createDepartmentsRoutes).
  const detailRoute = createRoute({
    getParentRoute: context.getParentRoute,
    path: `${context.basePath}/drivers/$id`,
    beforeLoad: requirePermission([DRIVERS_VIEW]),
    component: function DriverDetailRouteScreen() {
      const { id } = detailRoute.useParams() as unknown as { id: string };
      return createElement(DriverDetailScreen, {
        repository: drivers,
        basePath: context.basePath,
        driverId: id,
      });
    },
  });
  const editRoute = createRoute({
    getParentRoute: context.getParentRoute,
    path: `${context.basePath}/drivers/$id/edit`,
    beforeLoad: requirePermission([DRIVERS_UPDATE]),
    component: function DriverEditRouteScreen() {
      const { id } = editRoute.useParams() as unknown as { id: string };
      return createElement(DriverEditScreen, {
        repository: drivers,
        basePath: context.basePath,
        driverId: id,
        branches,
      });
    },
  });

  return [
    createRoute({
      getParentRoute: context.getParentRoute,
      path: `${context.basePath}/drivers`,
      validateSearch: driversSearchSchema,
      beforeLoad: requirePermission([DRIVERS_VIEW]),
      component: () =>
        createElement(DriversListScreen, {
          repository: drivers,
          basePath: context.basePath,
        }),
    }),
    // Before `$id`, so "archived" is never read as a driver id.
    createRoute({
      getParentRoute: context.getParentRoute,
      path: `${context.basePath}/drivers/archived`,
      validateSearch: archivedDriversSearchSchema,
      beforeLoad: requirePermission([DRIVERS_VIEW]),
      component: () =>
        createElement(ArchivedDriversListScreen, {
          repository: drivers,
          basePath: context.basePath,
        }),
    }),
    createRoute({
      getParentRoute: context.getParentRoute,
      path: `${context.basePath}/drivers/create`,
      beforeLoad: requirePermission([DRIVERS_CREATE]),
      component: () =>
        createElement(DriverCreateScreen, {
          repository: drivers,
          basePath: context.basePath,
          companies,
          branches,
        }),
    }),
    editRoute,
    detailRoute,
  ];
};
